import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Select, TextField } from 'mui-rff';
import {
  Alert,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  Search as SearchIcon,
  ReplayCircleFilled as ReplayCircleFilledIcon,
} from '@mui/icons-material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import ConfirmationDialog from '@components/ConfirmationDialog';
import { Typography } from '@components/styledComponents';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { viewMCCRates } from '../conversion-rates.breadcrumbs';

import {
  getMCCCurrencies,
  getMCCRates,
  selectIsFetchingMCCCurrencies,
  selectIsFetchingMCCRates,
  selectIsUpdatingMCCRates,
  selectMCCCurrencies,
  selectMCCRates,
  updateMCCRates,
} from '../store/mcc.slice';

import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';

import {
  MCC_COLUMNS,
  MCC_LEFT_COLUMN_STYLE,
} from '../conversion-rates.constants';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const initForm = { currency: 'MYR' };

function SearchForm({
  currencies,
  handleSubmit,
  invalid,
  isFetchingCurrencies,
  isFetchingRates,
  formErrorMsg,
  setFormErrorMsg,
}) {
  return (
    <form
      onSubmit={handleSubmit}
      noValidate>
      <Grid
        container
        spacing={2}
        alignItems="center">
        <Grid
          item
          xs={4}
          lg={2}>
          {currencies && currencies.length > 0 && (
            <Select
              required
              name="currency"
              size="small"
              variant="outlined"
              disabled={isFetchingCurrencies}
              label="Currency">
              {currencies.map((currency) => (
                <MenuItem
                  key={currency}
                  value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          )}
        </Grid>
        <Grid
          item
          xs={8}
          lg={4}>
          <TextField
            size="small"
            name="snapshotId"
            label="Conversion Snapshot ID"
            variant="outlined"
            InputProps={{ readOnly: isFetchingRates }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={2}>
          <LoadingButton
            fullWidth
            sx={{ minHeight: '40px' }}
            variant="contained"
            type="submit"
            disabled={invalid}
            loading={isFetchingCurrencies || isFetchingRates}
            startIcon={<SearchIcon />}
            loadingPosition="start">
            Search
          </LoadingButton>
        </Grid>
        {formErrorMsg && (
          <Grid
            item
            xs={12}>
            <Alert
              onClose={() => {
                setFormErrorMsg('');
              }}
              severity="error">
              {formErrorMsg}
            </Alert>
          </Grid>
        )}
      </Grid>
    </form>
  );
}

function LeftColumnHeader({ children }) {
  return (
    <TableCell
      component="th"
      scope="row"
      style={MCC_LEFT_COLUMN_STYLE}>
      {children}
    </TableCell>
  );
}

function Actions({ permissions, updateMCCRatesHandler }) {
  const isUpdatingMCCRates = useSelector(selectIsUpdatingMCCRates);
  const [ignoreVariance, setIgnoreVariance] = useState(false);
  const [showConfirmUpdateDialog, setShowConfirmUpdateDialog] = useState({
    show: false,
  });
  const { canUpdateMCCRates } = permissions;

  if (!canUpdateMCCRates) return null;

  return (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Actions
      </Typography>
      <Stack
        direction="row"
        sx={{ padding: '0 0 50px 0' }}>
        <FormControlLabel
          control={
            <Switch
              disabled={isUpdatingMCCRates}
              checked={ignoreVariance}
              onChange={() => setIgnoreVariance(!ignoreVariance)}
            />
          }
          label="Ignore Variance?"
        />
        <LoadingButton
          loading={isUpdatingMCCRates}
          variant="contained"
          onClick={() => {
            setShowConfirmUpdateDialog({ show: true });
          }}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Update Rates
        </LoadingButton>
      </Stack>
      <ConfirmationDialog
        openDialog={showConfirmUpdateDialog}
        yesHandler={() => updateMCCRatesHandler(ignoreVariance)}
        title="Update MCC Rates"
        message="Are you sure you want to update the rates?"
      />
    </>
  );
}

export default function MCCRates({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [displayRates, setDisplayRates] = useState([]);

  const isFetchingCurrencies = useSelector(selectIsFetchingMCCCurrencies);
  const isFetchingRates = useSelector(selectIsFetchingMCCRates);
  const mccRates = useSelector(selectMCCRates);
  const currencies = useSelector(selectMCCCurrencies);

  useEffect(() => {
    if (!permissions?.canViewMCCRates) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewMCCRates));
    dispatch(getMCCCurrencies());
    dispatch(getMCCRates(initForm));
  }, [dispatch]);

  useEffect(() => {
    if (
      !mccRates ||
      !mccRates.flightConversionRates ||
      !mccRates.baseConversionRates
    ) {
      setDisplayRates([]);
      return;
    }

    const {
      flightConversionRates: markUpRates,
      baseConversionRates: baseRates,
    } = mccRates;

    const rates = Object.entries(baseRates)
      .map(([currency, baseRate]) => ({
        currency,
        baseRate,
        markUpRate: markUpRates[currency],
      }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    setDisplayRates(rates);
  }, [mccRates]);

  const fetchMCCRates = (params) => {
    setFormErrorMsg('');

    if (!params) return;

    dispatch(getMCCRates(params))
      .then(({ payload, error }) => {
        if (payload.baseConversionRates) return;

        if (error?.message) {
          setFormErrorMsg(JSON.stringify(error.message));
          return;
        }

        if (payload?.message) {
          setFormErrorMsg(JSON.stringify(payload.message));
          return;
        }

        setFormErrorMsg('No rates found');
      })
      .catch((error) => {
        setFormErrorMsg(error);
        return { [FORM_ERROR]: error };
      });
  };

  const onSubmit = (values) => {
    if (!values.currency) return;

    fetchMCCRates(values);
  };

  const validate = (values) => {
    if (!values.currency) return { [FORM_ERROR]: 'Currency is required' };
    return {};
  };

  const updateMCCRatesHandler = (ignoreVariance) => {
    dispatch(updateMCCRates({ ignoreVariance }))
      .then(({ payload }) => {
        if (payload && payload.updateResult === 'SUCCESS') {
          dispatch(
            showSuccessMessage(
              `MCC rates updated successfully. Snapshot: ${payload.conversionSnapshotId}`,
            ),
          );
          dispatch(getMCCRates(initForm));
        } else {
          dispatch(
            showErrorMessage(
              payload.message ? payload.message : 'Failed to update MCC rates',
            ),
          );
        }
      })
      .catch((error) => {
        dispatch(showErrorMessage(error || 'Failed to update MCC rates'));
      });
  };

  return (
    <ApplicationPageTemplate
      pageReady={!isFetchingCurrencies}
      title="Multi-currency Conversion Rates">
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...initForm }}
        validate={validate}
        mutators={{
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          invalid,
          pristine,
          form: {
            mutators: { setValue },
          },
        }) => (
          <SearchForm
            currencies={currencies && currencies.length ? currencies : ['MYR']}
            handleSubmit={handleSubmit}
            values={values}
            pristine={pristine}
            invalid={invalid}
            isFetchingCurrencies={isFetchingCurrencies}
            isFetchingRates={isFetchingRates}
            formErrorMsg={formErrorMsg}
            setFormErrorMsg={setFormErrorMsg}
            setValue={setValue}
          />
        )}
      />
      {displayRates.length > 0 && (
        <TableContainer sx={{ marginTop: '50px', maxWidth: '500px' }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <LeftColumnHeader>Currency</LeftColumnHeader>
                <TableCell>{mccRates?.currency}</TableCell>
              </TableRow>
              <TableRow>
                <LeftColumnHeader>Conversion Snapshot Id</LeftColumnHeader>
                <TableCell> {mccRates?.conversionSnapshotId}</TableCell>
              </TableRow>
              {mccRates && mccRates.conversionSnapshotId && (
                <TableRow>
                  <LeftColumnHeader>Conversion Timestamp</LeftColumnHeader>
                  <TableCell>
                    {dayjs(parseFloat(mccRates.conversionSnapshotId))
                      .tz('Asia/Kuala_Lumpur', true)
                      .format('DD/MM/YYYY hh:mm A zzz')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Table
            sx={{ marginTop: '20px' }}
            stickyHeader
            size="small">
            <TableHead>
              <TableRow>
                {MCC_COLUMNS.map((column, index) => (
                  <TableCell
                    component="th"
                    key={column.id}
                    align={column.align}
                    style={{
                      ...column.styles,
                      paddingLeft: index === 0 ? 0 : '16px',
                    }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRates.map((row) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={row.currency}>
                  {MCC_COLUMNS.map((column, index) => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        component="th"
                        key={column.id}
                        style={{
                          ...column.styles,
                          paddingLeft: index === 0 ? 0 : '16px',
                        }}
                        align={column.align}>
                        {column.format && typeof value === 'number'
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Actions
        permissions={permissions}
        updateMCCRatesHandler={updateMCCRatesHandler}
      />
    </ApplicationPageTemplate>
  );
}
